import axios from "axios";
import API_BASE_URL from "../config/apiConfig";

// The Chats page's API. "scope" is CALL or CHAT (the team portals, with their own login) or ADMIN.
const client = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

const authHeader = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

const base = (scope) => {
  if (scope === "ADMIN") return "/api/admin/whatsapp";
  return scope === "CALL" ? "/api/call-team/whatsapp" : "/api/chat-team/whatsapp";
};

export const listChats = async (scope, token, { filter = "ALL", q = "", page = 0, size = 50 } = {}) => {
  const { data } = await client.get(`${base(scope)}/chats`, {
    headers: authHeader(token),
    params: { filter, q: q || undefined, page, size },
  });
  return data;
};

export const getChat = async (scope, token, chatId) => {
  const { data } = await client.get(`${base(scope)}/chats/${chatId}`, { headers: authHeader(token) });
  return data;
};

export const getChatMessages = async (scope, token, chatId, { beforeId, limit = 50 } = {}) => {
  const { data } = await client.get(`${base(scope)}/chats/${chatId}/messages`, {
    headers: authHeader(token),
    params: { beforeId: beforeId || undefined, limit },
  });
  return data;
};

export const markChatRead = async (scope, token, chatId, lastMessageId) => {
  await client.post(`${base(scope)}/chats/${chatId}/read`, { lastMessageId }, { headers: authHeader(token) });
};

export const searchChatLeadOptions = async (scope, token, q) => {
  const { data } = await client.get(`${base(scope)}/lead-options`, { headers: authHeader(token), params: { q } });
  return data;
};

export const linkChatToLead = async (scope, token, chatId, leadId, replaceAlternate = false) => {
  const { data } = await client.post(`${base(scope)}/chats/${chatId}/link`, { leadId, replaceAlternate }, { headers: authHeader(token) });
  return data;
};

export const markChatNotCustomer = async (scope, token, chatId, category, note) => {
  await client.put(`${base(scope)}/chats/${chatId}/not-customer`, { category, note }, { headers: authHeader(token) });
};

export const unmarkChatNotCustomer = async (scope, token, chatId) => {
  await client.delete(`${base(scope)}/chats/${chatId}/not-customer`, { headers: authHeader(token) });
};

export const prepareChatUpload = async (scope, token, chatId, file) => {
  const { data } = await client.post(`${base(scope)}/chats/${chatId}/media-upload`, file, { headers: authHeader(token) });
  return data;
};

// Straight to storage with the signed link, so it carries the file's type and not our login.
export const uploadChatFile = async (uploadUrl, file, contentType, onProgress) => {
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": contentType },
    onUploadProgress: (event) => {
      if (event.total && onProgress) onProgress(Math.round((event.loaded / event.total) * 100));
    },
  });
};

export const sendChatMessage = async (scope, token, chatId, message) => {
  const { data } = await client.post(`${base(scope)}/chats/${chatId}/messages`, message, { headers: authHeader(token) });
  return data;
};

export const retryChatMessage = async (scope, token, chatId, messageId) => {
  const { data } = await client.post(`${base(scope)}/chats/${chatId}/messages/${messageId}/retry`, null, { headers: authHeader(token) });
  return data;
};

export const getUrgentChats = async (scope, token) => {
  const { data } = await client.get(`${base(scope)}/urgent`, { headers: authHeader(token) });
  return data;
};

// Live updates: calls onChatUpdated(chatId) whenever a chat changes. Same streaming read as the
// Lead Tracker's lead updates, because EventSource can't send the login header.
export const connectChatEvents = async (scope, token, signal, onChatUpdated, onConnected) => {
  const response = await fetch(`${API_BASE_URL}${base(scope)}/events`, {
    headers: authHeader(token),
    signal,
  });
  if (!response.ok || !response.body) throw new Error("Could not connect to chat updates");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (!signal.aborted) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
    let boundary;
    while ((boundary = buffer.indexOf("\n\n")) >= 0) {
      const block = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const event = block.split("\n").find((line) => line.startsWith("event:"))?.slice(6).trim();
      const data = block.split("\n").find((line) => line.startsWith("data:"))?.slice(5).trim();
      if (event === "connected" && onConnected) onConnected();
      if (event === "chat-updated" && data) onChatUpdated(Number(data));
    }
  }
};
